import React from 'react';

// === Components === //
import {
  Accordion as AccordionContainer,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';

// === Helpers === //
import * as utils from '@utils';

// === Styles === //
import 'react-accessible-accordion/dist/fancy-example.css';
import styled from '@emotion/styled';
import { colors, dimensions, mixins } from '@styles';

// === Types === //
import { Variant } from '@content/types/layout';

const AccordionWrapper = styled.div<AccordionWrapperProps>`
  margin: 15px 0;

  .accordion {
    &__button {
      background-color: ${(props) =>
        props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault};
      color: ${colors.white};
      font-size: ${dimensions.fontSize.lg_md}px;
      padding: 16px 19px;
      font-weight: 700;
      position: relative;

      &::before {
        position: absolute;
        top: 0;
        bottom: 0;
        right: 23px;
        margin: auto;
        height: 8px;
        width: 8px;
        transform: rotate(45deg);
        ${mixins.transitionDefault};
      }

      &[aria-expanded='true'] {
        &::before {
          transform: rotate(-135deg);
        }
      }

      p {
        margin: 0;
      }

      &:hover,
      &:active {
        background-color: ${(props) =>
          props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault};
      }
    }

    &__panel {
      padding: 9px 16px 20px 16px;
      background-color: ${colors.ui.tableRow};
    }
  }

  &.type--symptoms {
    .accordion {
      &__button {
        background: ${colors.white};
        color: ${colors.ui.accordionSymptomsTitle};
        font-size: ${dimensions.fontSize.lg_md}px;
        padding: 16px 45px 16px 10px;
        font-weight: 700;
        position: relative;
        display: flex;
        box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.15);

        &:hover {
          background-color: ${colors.white};
        }

        .is-open {
          display: none;
        }
        .is-close {
          display: block;
        }

        > div {
          padding-left: 8px;
        }

        &[aria-expanded='true'] {
          background: ${(props) =>
            props.variant === 'blue' ? colors.ui.patientDefault : colors.ui.hcpDefault};
          color: ${colors.white};
          &::before {
            transform: rotate(-135deg);
          }
          .is-open {
            display: block;
          }
          .is-close {
            display: none;
          }
        }
      }

      &__panel {
        padding: 9px 16px 20px 16px;
        background-color: ${colors.ui.accordionBodyBg};
      }
    }
  }
`;

interface AccordionProps {
  title: string;
  variant: Variant;
  id: string;
  preExpanded?: string[];
  children: React.ReactChild;
  icons?: AccordionIcons;
  type?: 'table' | 'symptoms';
}

interface AccordionIcons {
  close: string;
  open: string;
}

interface AccordionWrapperProps {
  variant: Variant;
}

const Accordion = ({
  title,
  variant,
  id,
  preExpanded = [],
  children,
  icons,
  type = 'table',
}: AccordionProps) => {
  return (
    <AccordionWrapper className={`type--${type}`} variant={variant}>
      <AccordionContainer allowZeroExpanded preExpanded={preExpanded}>
        <AccordionItem uuid={id}>
          <AccordionItemHeading>
            <AccordionItemButton>
              {icons && icons.open !== '' ? <img className="is-open" src={icons.open} /> : null}
              {icons && icons.close !== '' ? <img className="is-close" src={icons.close} /> : null}
              {utils.SafeHtml(title)}
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel>{children}</AccordionItemPanel>
        </AccordionItem>
      </AccordionContainer>
    </AccordionWrapper>
  );
};

export default Accordion;
